<template>
  <div>
    <div class="calculator-container" :class="{ 'remove-margin': removeMargin }">
      <h1 class="h3">
        Kurtosis Calculator
      </h1>
      <div class="label-tooltip your-data-tooltip">
        <span class="your-data">
          Your Data
        </span>
        <div class="icon-info-tooltip">
          <IconInfo />
          <Tooltip>
            <span>Select <strong>Population</strong> if the data contains all measurable values or all values you are interested in.<br>
              Select <strong>Sample</strong> if the data is a sample of a large or unlimited population and you wish to make a statement about the entire population.</span>
          </Tooltip>
        </div>
      </div>
      <Dropdown
        class="calculation-method"
        calculator
        selected-title="Sample"
        :selected="selectedCalculationMethod"
        :radio-buttons="{ sample: 'Sample', population: 'Population' }"
        @change="currentSelectedCalculationMethod"
      />
      <form @submit.prevent="calculations">
        <div class="raw-data">
          <div class="label-tooltip">
            <label class="type-data">
              Enter Data
            </label>
            <div class="icon-info-tooltip kurtosis">
              <IconInfo />
              <Tooltip>
                <span>Input the data separated by comma, space or enter. To calculate kurtosis, you need at least four values in your dataset.</span>
              </Tooltip>
            </div>
          </div>
          <div class="textarea-border" :class="{ 'invalid-value': textareaAlert }">
            <textarea
              v-model="rawDataMean"
              cols="30" rows="10"
              placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
            />
          </div>
          <div v-if="textareaAlert" class="error">
            {{ textareaAlert }}
          </div>
        </div>
        <div class="decimal-places">
          <div class="label-tooltip">
            <label>
              Decimal Places
            </label>
            <div class="icon-info-tooltip kurtosis">
              <IconInfo />
              <Tooltip>
                <span>Choose the number of decimal places.</span>
              </Tooltip>
            </div>
          </div>
          <Dropdown
            calculator
            calculator-decimal-places
            class="small-dropdown"
            :selected="selectedDecimalPlaces"
            :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
            @change="currentSelectedDecimalPlace"
          />
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>
    <div
      v-show="kurtosis" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div>
          <span class="result-name">Kurtosis:</span>
          <span v-if="kurtosis !== 'NaN'" class="result-value">{{ kurtosis }} ({{ currentDistribution }})</span>
          <span v-else class="result-value">{{ kurtosis }}</span>
        </div>
      </div>
    </div>
    <div v-if="kurtosis && kurtosis !== 'NaN'" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p v-if="kurtosis === 0">
        The distribution has an excess kurtosis of 0, indicating that it is <a class="colored-texts" data-section="heading4" @click="scrollToSectionArticles">mesokurtic</a> and has the same amount of tail weight and peakedness as a normal distribution.
      </p>
      <p v-else-if="kurtosis > 0 && kurtosis <= 1">
        The distribution is <a class="colored-texts" data-section="heading3" @click="scrollToSectionArticles">leptokurtic</a>. It has a <span class="colored-texts">low positive kurtosis</span>, indicating that it is <span class="colored-texts">more peaked</span> and has <span class="colored-texts">more outliers</span> than a normal distribution.
      </p>
      <p v-else-if="kurtosis >= -1 && kurtosis < 0">
        The distribution is <a class="colored-texts" data-section="heading5" @click="scrollToSectionArticles">platykurtic</a>. It has a <span class="colored-texts">low negative kurtosis</span>, indicating that it is <span class="colored-texts">relatively flat</span> and has <span class="colored-texts">fewer</span> outliers than a normal distribution.
      </p>
      <p v-else-if="kurtosis > 1">
        The distribution is <a class="colored-texts" data-section="heading3" @click="scrollToSectionArticles">leptokurtic</a>. It has a <span class="colored-texts">high positive kurtosis</span>, indicating that it is <span class="colored-texts">very peaked</span> and has a relatively large number of outliers.
      </p>
      <p v-else-if="kurtosis < -1">
        The distribution is <a class="colored-texts" data-section="heading5" @click="scrollToSectionArticles">platykurtic</a>. It has a <span class="colored-texts">high negative kurtosis</span>, indicating that it is <span class="colored-texts">very flat</span> and has a relatively low number of outliers.
      </p>
    </div>
  </div>
</template>

<script>
import calculatorsMainMixin from '@itbpbg/lms-components/src/mixins/calculators/main';
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';

export default {
  components: {
    Dropdown, Tooltip, IconInfo,
  },
  mixins: [calculatorsMainMixin],
  data() {
    return {
      kurtosis: '',
      rawDataMeanArray: [],
      rawDataMean: '',
      selectedCalculationMethod: 'sample',
      textareaAlert: false,
      selectedDecimalPlaces: '4',
      removeMargin: false,
      resultNumber: 0,
    };
  },
  computed: {
    currentDistribution() {
      if (this.kurtosis === 0) {
        return 'Mesokurtic';
      }
      if ((this.kurtosis > 0 && this.kurtosis <= 1) || this.kurtosis > 1) {
        return 'Leptokurtic';
      }
      if ((this.kurtosis >= -1 && this.kurtosis < 0) || this.kurtosis < -1) {
        return 'Platykurtic';
      }

      return '';
    },
    hideResultVariablesArray() {
      return [
        this.rawDataMean,
        this.selectedDecimalPlaces,
      ];
    },
  },
  watch: {
    selectedCalculationMethod() {
      this.defaultingInput();
    },
    hideResultVariablesArray() {
      this.hideResults();
    },
  },
  methods: {
    scrollToSectionArticles(e) {
      let yOffset;
      if (this.$store.state.layout === 'desktop') {
        yOffset = -90;
      } else {
        yOffset = this.$config.project === 'lms' ? -64 : -68;
      }

      const section = `${e?.target?.getAttribute('data-section') || e?.target?.closest('li')?.getAttribute('data-section')}`;
      const element = document.querySelector(`#${section}`);
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      setTimeout(() => {
        this.clickedSection = section;
        window.scrollTo({ top: y, behavior: 'smooth' });

        setTimeout(() => {
          this.clickedSection = null;
        }, 500);
      }, 200);
    },
    reset(e) {
      this.selectedCalculationMethod = 'sample';
      this.defaultingInput();
      e.preventDefault();
    },
    defaultingInput() {
      this.rawDataMean = '';
      this.selectedDecimalPlaces = '4';
      this.kurtosis = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    hideResults() {
      this.removeMargin = false;
      this.kurtosis = '';
      this.$store.commit('calculators/result', false);
      this.textareaAlert = '';
    },
    loadExample(e) {
      this.textareaAlert = '';
      this.selectedDecimalPlaces = '4';
      this.removeMargin = false;
      this.rawDataMean = '6\n7\n5\n7\n5\n4\n4\n6\n1\n3\n2\n8';
      this.kurtosis = '';
      this.$store.commit('calculators/result', false);

      e.preventDefault();
    },
    inputValidation() {
      this.textareaAlert = '';
      this.rawDataMeanArray = this.rawDataMean.split(/,|\s+|\n+/).filter((value) => value !== '');

      if (this.rawDataMeanArray.length === 0) {
        this.textareaAlert = 'Field required';
        this.rawDataMean = '';
        return false;
      }

      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        if (this.rawDataMeanArray[i] < 0 || Number.isNaN(Number(this.rawDataMeanArray[i]))) {
          this.textareaAlert = 'Invalid value';
          return false;
        }
      }

      if (this.rawDataMeanArray.length < 4 && this.rawDataMeanArray.length > 0) {
        this.textareaAlert = 'To calculate kurtosis, you need at least four values in your dataset.';

        return false;
      }

      return true;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      let mean = 0;
      let size = 0;
      let standardDeviation = 0;
      const standardizationsArray = [];
      let cubedStandardizationsArray = [];
      let raisedToFourStandardizationsValuesSum = 0;
      let kurtosis = 0;

      // Calculating the mean
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        mean += Number(this.rawDataMeanArray[i]);
      }

      mean /= this.rawDataMeanArray.length;

      // Get the size
      size = this.rawDataMeanArray.length;

      // Calculating sample/population standard deviation
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        standardDeviation += (Number(this.rawDataMeanArray[i]) - mean) ** 2;
      }

      if (this.selectedCalculationMethod === 'sample') {
        standardDeviation = Math.sqrt(standardDeviation / (size - 1));
      } else {
        standardDeviation = Math.sqrt(standardDeviation / size);
      }

      // Standardization
      for (let i = 0; i < this.rawDataMeanArray.length; i += 1) {
        standardizationsArray.push((this.rawDataMeanArray[i] - mean) / standardDeviation);
      }

      // Raise standardized values to the power of 4
      cubedStandardizationsArray = standardizationsArray.map((num) => num ** 4);

      // Calculate raised to four standardized values sum
      raisedToFourStandardizationsValuesSum = cubedStandardizationsArray.reduce((a, b) => a + b, 0);

      // Calculate sample/population kurtosis
      if (this.selectedCalculationMethod === 'sample') {
        kurtosis = ((size * (size + 1)) / ((size - 1) * (size - 2) * (size - 3))) * raisedToFourStandardizationsValuesSum;
        kurtosis -= (3 * ((size - 1) ** 2)) / ((size - 2) * (size - 3));
      } else {
        kurtosis = ((1 / size) * raisedToFourStandardizationsValuesSum) - 3;
      }

      // Formatting result
      this.kurtosis = kurtosis.toFixed(this.selectedDecimalPlaces);

      const onlyZerosTest = /^\d+.[0]+$/;
      if (onlyZerosTest.test(this.kurtosis)) {
        this.kurtosis = Math.trunc(this.kurtosis).toString();
      }

      this.removeMargin = true;
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.calculationData(mean, size, standardDeviation, raisedToFourStandardizationsValuesSum);
      this.scrollToSection();
    },
    calculationData(mean, size, standardDeviation, raisedToFourStandardizationsValuesSum) {
      this.$store.commit('calculators/calculationData',
        {
          method: this.selectedCalculationMethod,
          rawDataMeanArray: this.rawDataMeanArray,
          mean,
          size,
          standardDeviation,
          selectedDecimalPlaces: this.selectedDecimalPlaces,
          raisedToFourStandardizationsValuesSum: raisedToFourStandardizationsValuesSum.toFixed(this.selectedDecimalPlaces),
          kurtosis: this.kurtosis,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .raw-data {
    .label-tooltip .icon-info-tooltip {
      &.kurtosis {
        &:hover {
          .tooltip {
            height: auto;
          }
        }
      }
    }
  }
}

.summary-container {
  min-height: unset !important;

  .results {
    min-height: unset !important;

    div {
      justify-content: space-between;
      padding: 0 15px 0 10px;
    }
  }
}

.interpretation-container {
  p {
    margin: 0;

    ::v-deep .colored-texts {
      font-weight: 600;
      color: var(--accent-shade-3);
    }

    ::v-deep a {
      text-decoration: underline;
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    &.remove-margin {
      margin-bottom: 0;
    }

    .decimal-places {
      .label-tooltip .icon-info-tooltip {
        &.kurtosis {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.kurtosis {
          &:hover {
            .tooltip {
              left: 130%;

              &:after {
                left: 42.15%;
              }
            }
          }
        }
      }
    }
  }

  div .summary-container {
    margin-top: var(--spacing-60);
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      width: 100%;

      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.kurtosis {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .raw-data {
      .label-tooltip .icon-info-tooltip {
        &.kurtosis {
          &:hover {
            .tooltip {
              left: 271%;

              &:after {
                left: 34.5%;
              }
            }
          }
        }
      }
    }
  }

  div .interpretation-container {
    padding: var(--spacing-32) var(--spacing-20);
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
